/**
 * Billing Configuration - Package Tiers and Pricing
 * Defines the SaaS billing packages based on farm size
 */

export const PACKAGE_TIERS = {
	FREE: {
		name: "Free",
		id: "free",
		minAnimals: 0,
		maxAnimals: 50,
		monthlyPrice: 0,
		features: [
			"Basic animal tracking",
			"Health monitoring",
			"Treatment management",
			"Production data tracking"
		]
	},
	SMALL: {
		name: "Small Farm",
		id: "small",
		minAnimals: 51,
		maxAnimals: 99,
		monthlyPrice: 9.99,
		features: [
			"Everything in Free",
			"Advanced analytics",
			"Work planning",
			"Priority support"
		]
	},
	MEDIUM: {
		name: "Medium Farm",
		id: "medium",
		minAnimals: 100,
		maxAnimals: 499,
		monthlyPrice: 19.99,
		features: [
			"Everything in Small",
			"Custom reporting",
			"API access",
			"Dedicated support"
		]
	},
	LARGE: {
		name: "Large Farm",
		id: "large",
		minAnimals: 500,
		maxAnimals: 999,
		monthlyPrice: 49.99,
		features: [
			"Everything in Medium",
			"Custom integrations",
			"On-premise options",
			"24/7 support"
		]
	},
	ENTERPRISE: {
		name: "Enterprise",
		id: "enterprise",
		minAnimals: 1000,
		maxAnimals: Infinity,
		monthlyPrice: null, // Custom pricing
		features: [
			"Everything in Large",
			"Custom integrations",
			"On-premise options",
			"24/7 support",
			"Custom SLA"
		]
	}
};

/**
 * Trial configuration
 */
export const TRIAL_CONFIG = {
	durationMonths: 2,
	durationDays: 60, // 2 months
	freePlanAfterTrial: false // If false, requires subscription after trial
};

/**
 * Payment validation limits
 * Used for security validation of payment amounts
 */
export const PAYMENT_LIMITS = {
	MIN_AMOUNT: 0.50, // Stripe minimum charge
	MAX_AMOUNT: 100.00, // Maximum based on highest tier (large: 49.99)
	CURRENCY: 'EUR',
	// Valid monthly subscription prices from tier config
	VALID_PRICES: [0, 9.99, 19.99, 49.99]
};

/**
 * Validates a payment amount
 * @param {number} amount - Amount to validate
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validatePaymentAmount(amount) {
	if (typeof amount !== 'number' || isNaN(amount)) {
		return { valid: false, error: 'Amount must be a valid number' };
	}

	if (amount < PAYMENT_LIMITS.MIN_AMOUNT) {
		return { valid: false, error: `Minimum payment amount is ${PAYMENT_LIMITS.MIN_AMOUNT} ${PAYMENT_LIMITS.CURRENCY}` };
	}

	if (amount > PAYMENT_LIMITS.MAX_AMOUNT) {
		return { valid: false, error: `Maximum payment amount is ${PAYMENT_LIMITS.MAX_AMOUNT} ${PAYMENT_LIMITS.CURRENCY}` };
	}

	return { valid: true };
}

/**
 * Determines the required package tier based on farm size
 * @param {number} farmSize - Number of active animals
 * @returns {Object} Package tier configuration
 */
export function calculateRequiredPackage(farmSize) {
	if (farmSize === 0) {
		return PACKAGE_TIERS.FREE; // Default for new users
	}
	
	if (farmSize <= PACKAGE_TIERS.FREE.maxAnimals) {
		return PACKAGE_TIERS.FREE;
	} else if (farmSize < PACKAGE_TIERS.MEDIUM.minAnimals) {
		return PACKAGE_TIERS.SMALL;
	} else if (farmSize < PACKAGE_TIERS.LARGE.minAnimals) {
		return PACKAGE_TIERS.MEDIUM;
	} else if (farmSize < PACKAGE_TIERS.ENTERPRISE.minAnimals) {
		return PACKAGE_TIERS.LARGE;
	} else {
		return PACKAGE_TIERS.ENTERPRISE;
	}
}

/**
 * Gets all available package tiers
 * @returns {Array} Array of package tier objects
 */
export function getAllPackages() {
	return Object.values(PACKAGE_TIERS);
}

/**
 * Gets a package by its ID
 * @param {string} packageId - Package ID (small, medium, large, enterprise)
 * @returns {Object|null} Package tier configuration or null if not found
 */
export function getPackageById(packageId) {
	return Object.values(PACKAGE_TIERS).find(pkg => pkg.id === packageId) || null;
}

/**
 * Checks if a farm size fits within a package tier
 * @param {number} farmSize - Number of animals
 * @param {string} packageId - Package ID to check
 * @returns {boolean} True if farm size fits within package limits
 */
export function isFarmSizeWithinPackage(farmSize, packageId) {
	const pkg = getPackageById(packageId);
	if (!pkg) return false;
	
	return farmSize >= pkg.minAnimals && farmSize <= pkg.maxAnimals;
}

/**
 * Checks if a MAC address is a placeholder (SaaS user) or real eartag
 * @param {string} mac - MAC address to check
 * @returns {boolean} True if MAC is a placeholder
 */
export function isPlaceholderMAC(mac) {
	return mac && (mac.startsWith("PLACEHOLDER:") || mac.startsWith("00:00:00:"));
}

/**
 * Checks if user data indicates an eartag user (has physical eartags)
 * @param {Object} userData - User document data
 * @returns {boolean} True if user is an eartag user
 */
export function isEartagUser(userData) {
	// If noEartags flag doesn't exist or is false, user has eartags
	return !userData?.noEartags;
}

/**
 * Checks if a billing tier requires payment (not free)
 * @param {string} tierId - Package tier ID (free, small, medium, large, enterprise)
 * @returns {boolean} True if tier requires payment
 */
export function requiresPayment(tierId) {
	if (!tierId) return false;
	
	const tier = getPackageById(tierId);
	if (!tier) return false;
	
	// Free tier and enterprise (custom pricing) don't require upfront payment
	// But enterprise will still need billing info for custom pricing setup
	return tier.id !== "free" && tier.monthlyPrice !== null && tier.monthlyPrice > 0;
}

